<script lang="ts">
	import { CityFeatureType } from '$lib/features/cityFeature/cityFeatureType.enum';
	import { deviceScreen } from '$lib/state/deviceScreen.svelte';

	export type AllKey = 'ALL';
	export type TypeKey = AllKey | CityFeatureType;
	export interface TypeFilterOption {
		type: TypeKey;
		label: string;
		count: number;
	}
	function defineSortOrder<T extends readonly TypeKey[]>(order: T & { [K in Exclude<TypeKey, T[number]>]: never }): T {
		return order;
	}

	export const SORT_ORDER = defineSortOrder([
		'ALL',
		CityFeatureType.CITY_DETAILS,
		CityFeatureType.ATTRACTION,
		CityFeatureType.GREEN_SPACE,
		CityFeatureType.UNESCO_SITE,
		CityFeatureType.CITY_HIGHLIGHT,
		CityFeatureType.PHOTO_SPOT,
		CityFeatureType.VIEWPOINT,
		CityFeatureType.FREE_THING_TO_DO,
		CityFeatureType.HIDDEN_GEM,
		CityFeatureType.ARCHITECTURAL_HIGHLIGHT,
		CityFeatureType.NIGHTLIFE_SPOT,
		CityFeatureType.ANNUAL_FESTIVAL,
		CityFeatureType.SCENIC_STROLL,
		CityFeatureType.SCENIC_BIKE_RIDE,
		CityFeatureType.SELF_GUIDED_TOUR,
		CityFeatureType.MARKET,
		CityFeatureType.ITINERARY,
		CityFeatureType.ACTIVITY,
		CityFeatureType.FOOD,
		CityFeatureType.SEASONAL_HIGHLIGHT
	] as const);

	let {
		options,
		selectedType = $bindable<TypeKey>(),
		onTypeSelect
	}: { options: TypeFilterOption[]; selectedType: TypeKey; onTypeSelect: (type: TypeKey) => void } = $props();

	const sortedOptions = $derived<TypeFilterOption[]>(
		options.sort((a, b) => {
			const indexA = SORT_ORDER.indexOf(a.type);
			const indexB = SORT_ORDER.indexOf(b.type);
			const posA = indexA === -1 ? 999 : indexA;
			const posB = indexB === -1 ? 999 : indexB;
			return posA - posB;
		})
	);
</script>

{#if deviceScreen.isMobile}
	<ul class="pointer-events-auto flex w-full snap-x snap-mandatory scrollbar-none gap-1.5 overflow-x-auto py-1">
		{#each sortedOptions as option (option.type)}
			<li class="shrink-0 snap-start">
				{@render MobileFilters(option)}
			</li>
		{/each}
	</ul>
{:else}
	<div
		class="pointer-events-auto flex max-h-full flex-col overflow-hidden rounded-box bg-base-100/97 p-4 text-base-content shadow-xl"
	>
		<h2 class="mb-2 shrink-0 px-1 text-xs tracking-widest text-base-content/70">FILTER MAP SIGHTS</h2>
		<div class="min-h-0 flex-1 scrollbar-thin overflow-y-auto pr-1.5">
			<ul class="flex flex-col gap-1">
				{#each sortedOptions as option (option.type)}
					<li>
						{@render DesktopFilters(option)}
					</li>
				{/each}
			</ul>
		</div>
	</div>
{/if}

{#snippet MobileFilters(option: { type: CityFeatureType | 'ALL'; label: string; count: number })}
	<label class="pointer-events-auto">
		<input
			bind:group={selectedType}
			class="hidden"
			type="radio"
			name="map-layer-filter-mobile"
			value={option.type}
			checked={selectedType === option.type}
			onchange={() => onTypeSelect(selectedType)}
		/>
		<span
			class={{
				'badge gap-1 border-base-content/10 text-xs badge-lg font-bold shadow-md transition-all select-none': true,
				'border-primary bg-primary text-primary-content': selectedType === option.type,
				'bg-base-100/85 text-base-content backdrop-blur-md': selectedType !== option.type
			}}
		>
			<span>{option.label}</span>
			<span
				class={{
					'font-mono text-xs font-black': true,
					'text-primary-content/70': selectedType === option.type,
					'text-base-content/70': selectedType !== option.type
				}}>({option.count})</span
			>
		</span>
	</label>
{/snippet}

{#snippet DesktopFilters(option: { type: CityFeatureType | 'ALL'; label: string; count: number })}
	<label
		class={{
			'flex cursor-pointer items-center justify-between gap-3 rounded-field px-2.5 py-1.5 transition-colors': true,
			'bg-primary/10 font-semibold text-primary': selectedType === option.type,
			'text-base-content/90 hover:bg-base-content/5': selectedType !== option.type
		}}
	>
		<div class="flex items-center gap-2">
			<input
				bind:group={selectedType}
				class="radio radio-xs radio-primary"
				type="radio"
				name="map-layer-filter-desktop"
				value={option.type}
				checked={selectedType === option.type}
				onchange={() => onTypeSelect(selectedType)}
			/>
			<span class="text-xs tracking-wide select-none">{option.label}</span>
		</div>
		<span
			class={{
				'rounded-md px-1.5 py-0.5 font-mono text-xs font-bold': true,
				'bg-primary/10 text-primary': selectedType === option.type,
				'text-base-content/70': selectedType !== option.type
			}}
		>
			{option.count}
		</span>
	</label>
{/snippet}
